import { CompanyWithSoiForOrg, SoiAllPeriodsLists } from "../../soi/dto/SoIAuxiliary";

/**
 * Generates an Excel SUM formula for a given field over a range of rows.
 *
 * The formula is constructed using the column letter (calculated from the field name)
 * and a vertical range of rows based on the provided start index and range length.
 *
 * @param headers - Array of header strings that map to Excel columns.
 * @param field - The name of the field whose column will be summed.
 * @param rowIndex - The starting row index (zero-based).
 * @param addLen - Number of rows to include in the SUM range.
 * @returns A string representing the Excel SUM formula (e.g., 'SUM(B5:B10)').
 *
 * @example
 * headers = ['Date', 'Investment', 'Fair value'];
 * field = 'Investment';
 * rowIndex = 4;
 * addLen = 6;
 * generateSumFormula(headers, field, rowIndex, addLen); // => 'SUM(B5:B10)'
 */
export const generateSumFormula = (headers: string[], field: string, rowIndex: number, addLen: number): string => {
  const index = headers.indexOf(field);
  const letter = columnIndexToExcelLetter(index);
  return `SUM(${letter}${rowIndex + 1}:${letter}${rowIndex + addLen})`;
};

/**
 * Generates an Excel formula to calculate gain/loss as the difference between
 * fair market value (FMV) and cost for a specific row.
 *
 * The formula uses Excel column letters derived from the provided headers array.
 * Example output: 'D10-C10'
 *
 * @param headers - Array of header strings that map to Excel columns.
 * @param costName - The header name for the cost column.
 * @param fmvName - The header name for the fair market value column.
 * @param rowIndex - The row index (1-based Excel row number).
 * @returns A string representing the Excel formula for gain/loss.
 *
 * @example
 * headers = ['Investment', 'Cost', 'Fair value'];
 * costName = 'Cost';
 * fmvName = 'Fair value';
 * rowIndex = 10;
 * generateGainLossFormula(headers, costName, fmvName, rowIndex); // => 'C10-B10'
 */
export const generateGainLossFormula = (headers: string[], costName: string, fmvName: string, rowIndex: number): string => {
  const indexCost = headers.indexOf(costName);
  const letterCost = columnIndexToExcelLetter(indexCost);
  const indexFmv = headers.indexOf(fmvName);
  const letterFmv = columnIndexToExcelLetter(indexFmv);
  return `${letterFmv}${rowIndex}-${letterCost}${rowIndex}`;
};

/**
 * Generates an Excel formula to calculate MOIC (Multiple on Invested Capital)
 * as the ratio of fair market value (FMV) to cost for a specific row.
 *
 * The formula uses Excel column letters derived from the provided headers array
 * and wraps the result in an IFERROR to safely return 0 in case of division errors.
 * Example output: 'IFERROR(D10/C10,0)'
 *
 * @param headers - Array of header strings that map to Excel columns.
 * @param costName - The header name for the cost/investment column.
 * @param fmvName - The header name for the fair market value column.
 * @param rowIndex - The row index (1-based Excel row number).
 * @returns A string representing the Excel formula for MOIC.
 *
 * @example
 * headers = ['Investment', 'Cost', 'Fair value'];
 * costName = 'Cost';
 * fmvName = 'Fair value';
 * rowIndex = 10;
 * generateMoicFormula(headers, costName, fmvName, rowIndex); // => 'IFERROR(C10/B10,0)'
 */
export const generateMoicFormula = (headers: string[], costName: string, fmvName: string, rowIndex: number): string => {
  const indexCost = headers.indexOf(costName);
  const letterCost = columnIndexToExcelLetter(indexCost);
  const indexFmv = headers.indexOf(fmvName);
  const letterFmv = columnIndexToExcelLetter(indexFmv);
  return `IFERROR(${letterFmv}${rowIndex}/${letterCost}${rowIndex},0)`;
};

/**
 * Converts a zero-based column index to its corresponding Excel column letter.
 *
 * Excel columns are labeled alphabetically:
 * - 0 => 'A'
 * - 1 => 'B'
 * - ...
 * - 27 => 'AB'
 * - ...
 *
 * @param index - The zero-based column index (e.g., 0 for 'A', 1 for 'B', ..., 26 for 'AA').
 * @returns The Excel-style column letter(s).
 *
 * @example
 * exampleArray = ['Date', 'Investment', 'Fair value]
 * let index = exampleArray.indexOf('Investment') => 1
 * columnIndexToExcelLetter(index);   // "B"
 * let index = exampleArray.indexOf('Fair value') => 2
 * columnIndexToExcelLetter(index);  => "C"
 * columnIndexToExcelLetter(27);  => "AB"
 */
export const columnIndexToExcelLetter = (index: number): string => {
  let letter = '';
  let temp = index;

  while (temp >= 0) {
    letter = String.fromCharCode((temp % 26) + 65) + letter;
    temp = Math.floor(temp / 26) - 1;
  }

  return letter;
};

/**
 * Generates an Excel SUM formula for a specific field across multiple rows (e.g., for subtotals or grand totals).
 *
 * The formula uses the field's column letter and row indexes provided in `totalIndexArray`.
 *
 * @param field - The name of the header whose column will be summed.
 * @param headers - Array of header strings that map to Excel columns.
 * @param totalIndexArray - An array of row indexes (1-based) to include in the SUM formula.
 * @returns A string representing the Excel SUM formula or '0' if the array is empty.
 *
 * @example
 * headers = ['Company', 'Investment', 'Fair value'];
 * field = 'Investment';
 * totalIndexArray = [5, 10, 15, 19];
 * generateExcelSumFormulaForGrandOrSubTotal(field, headers, totalIndexArray); // => 'SUM(B5,B10,B15,B19)'
 */
export const generateExcelSumFormulaForGrandOrSubTotal = (field: string, headers: string[], totalIndexArray: number[]): string => {
  const columnIndex = headers.indexOf(field);
  const letter = columnIndexToExcelLetter(columnIndex);
  if (!totalIndexArray || totalIndexArray.length === 0) {
    return '0';
  }
  const columnReference = letter;
  return `SUM(${totalIndexArray.map((index) => `${columnReference}${index}`).join(',')})`;
};

export const nullToUndefined = (value: number): number => (value ? value : undefined);

/**
 * Returns an array of row indexes for each fund block inside a company structure (`soi`).
 *
 * This is useful when summing values across multiple funds or highlighting fund-level rows in Excel.
 *
 * @param soi - The company object (`CompanyWithSoiForOrg`) containing funds[] and their rounds[].
 * @returns An array of row indexes (1-based) where each fund starts.
 *
 * @example
 * currentRowIndex = 5;
 * soi.fundsSoi = [
 *   { rounds: [r1, r2] },    // Fund 1 -> starts at 6, adds 3 rows (2 rounds + fund row)
 *   { rounds: [r1] },        // Fund 2 -> starts at 9, adds 2 rows (1 round + fund row)
 * ];
 * getFundRowIndexesForSoi(soi); // => [6, 9]
 */
export const getFundRowIndexesForSoi = (soi: CompanyWithSoiForOrg, currentRowIndex: number): number[] => {
  const fundRowIndexes: number[] = [];

  let rowIndex = currentRowIndex + 1;

  for (let i = 0; i < soi.fundsSoi.length; i++) {
    const fund = soi.fundsSoi[i];

    fundRowIndexes.push(rowIndex);

    rowIndex += fund.rounds.length + 1 || 1;
  }

  return fundRowIndexes;
};

/**
 * Extracts a sorted list of unique period values from both active and realized SOI lists.
 *
 * @param allPeriods - An object containing both active and realized lists of OrgSoiByPeriodDTO.
 * @returns A sorted array of unique period strings in descending chronological order.
 *
 * @example
 * const periods = getUniquePeriodsForSoiAllPeriods({
 *   active: [{ period: '2024-05-01', soi: [...] }],
 *   realized: [{ period: '2023-12-01', soi: [...] }]
 * });
 * // returns ['2024-05-01', '2023-12-01']
 */
export const getUniquePeriodsForSoiAllPeriods = (allPeriods: SoiAllPeriodsLists): string[] => {
  const all = [...allPeriods.active, ...allPeriods.realized];

  const uniqueTimestamps = new Set<string>();

  all.forEach((item) => {
    uniqueTimestamps.add(item.period.toString());
  });
  return Array.from(uniqueTimestamps).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  );
};
